import * as Yup from 'yup';
import moment from 'moment';
import { WorkerProfileField } from 'types';
import { IFile } from 'models/general';
import { GOV_ID_TYPE_OTHER_OPTION_ID } from 'constants/common';
import { shouldEnforceFieldAsMandatory } from 'utils/skippableFieldsUtils';
import { minLengthIfFilled } from 'utils';

export interface IPersonalInformationSchema {
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string | null;
  gender: number | null;
  ethnicityId?: string | null;
  primaryLanguageId: string | null;
  isVeteran: boolean | null;
  allergies: string | null;
  socialSecurityNumber: string | null;
  identificationTypeId?: string | null;
  identificationGeographicLocationId?: string | null;
  identificationNumber?: string | null;
  otherIdentificationType?: string | null;
  governmentDocumentFiles?: Array<IFile>;
  pictureUrl?: string | null;
}

const personalInformationSchema = (t: any, profileFieldConstraintsMap: any, hasReachedConsentForm: boolean = false) =>
  Yup.object().shape({
    firstName: Yup.string().when([], {
      is: () =>
        shouldEnforceFieldAsMandatory(
          WorkerProfileField.workerFirstName,
          profileFieldConstraintsMap,
          hasReachedConsentForm,
        ),
      then: schema => schema.required(t('personalInformation.errors.firstNameRequired')).trim(),
    }),

    middleName: Yup.string()
      .nullable()
      .notRequired()
      .when([], {
        is: () =>
          shouldEnforceFieldAsMandatory(
            WorkerProfileField.workerMiddleName,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
          ),
        then: schema => schema.required(t('personalInformation.errors.middleNameRequired')).trim(),
      }),

    lastName: Yup.string().when([], {
      is: () =>
        shouldEnforceFieldAsMandatory(
          WorkerProfileField.workerLastName,
          profileFieldConstraintsMap,
          hasReachedConsentForm,
        ),
      then: schema => schema.required(t('personalInformation.errors.lastNameRequired')).trim(),
    }),

    dateOfBirth: Yup.mixed()
      .nullable()
      .test('is-valid-date', t('basicInformation.errors.invalidDate'), value => {
        if (value) {
          if (moment.isMoment(value)) {
            return value.isValid();
          }
          // Accept string or Date which moment can parse
          if (typeof value === 'string' || value instanceof Date) {
            return moment(value).isValid();
          }
          return false;
        } else {
          return true;
        }
      })
      .when([], {
        is: () =>
          shouldEnforceFieldAsMandatory(
            WorkerProfileField.workerDateOfBirth,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
          ),
        then: schema => schema.required(t('personalInformation.errors.dateOfBirthRequired')),
      }),

    gender: Yup.number()
      .nullable()
      .when([], {
        is: () =>
          shouldEnforceFieldAsMandatory(
            WorkerProfileField.workerGender,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
          ),
        then: schema => schema.required(t('personalInformation.errors.genderRequired')),
      }),

    ethnicityId: Yup.string()
      .nullable()
      .when([], {
        is: () =>
          shouldEnforceFieldAsMandatory(
            WorkerProfileField.workerEthnicity,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
          ),
        then: schema => schema.required(t('personalInformation.errors.ethnicityRequired')),
      }),

    primaryLanguageId: Yup.string()
      .nullable()
      .when([], {
        is: () =>
          shouldEnforceFieldAsMandatory(
            WorkerProfileField.workerPrimaryLanguage,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
          ),
        then: schema => schema.required(t('personalInformation.errors.primaryLanguageRequired')),
      }),

    isVeteran: Yup.boolean()
      .nullable()
      .when([], {
        is: () =>
          shouldEnforceFieldAsMandatory(
            WorkerProfileField.workerVeteranStatus,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
          ),
        then: schema => schema.required(t('personalInformation.errors.veteranStatusRequired')),
      }),

    allergies: Yup.string()
      .nullable()
      .when([], {
        is: () =>
          shouldEnforceFieldAsMandatory(
            WorkerProfileField.workerAllergies,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
          ),
        then: schema => schema.required(t('personalInformation.errors.allergiesRequired')),
      }),

    socialSecurityNumber: Yup.string()
      .nullable()
      .when([], {
        is: () =>
          shouldEnforceFieldAsMandatory(
            WorkerProfileField.workerLastSSN,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
          ),
        then: schema =>
          schema
            .required(t('personalInformation.errors.lastSSNRequired'))
            .matches(/^\d{4}$/, t('personalInformation.errors.invalidSSN')),
      }),

    // Government ID Fields
    identificationTypeId: Yup.string()
      .nullable()
      .when([], {
        is: () =>
          shouldEnforceFieldAsMandatory(
            WorkerProfileField.workerGovtIdType,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
          ),
        then: schema => schema.required(t('personalInformation.errors.idTypeRequired')),
      }),

    identificationGeographicLocationId: Yup.string()
      .nullable()
      .when(['identificationTypeId'], {
        is: val =>
          val !== GOV_ID_TYPE_OTHER_OPTION_ID &&
          shouldEnforceFieldAsMandatory(
            WorkerProfileField.workerGovtIdIssuedBy,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
          ),
        then: schema => schema.required(t('personalInformation.errors.issuedByRequired')),
      }),

    // When "Other" is selected for ID Type
    otherIdentificationType: Yup.string()
      .nullable()
      .when(['identificationTypeId'], {
        is: val => val === GOV_ID_TYPE_OTHER_OPTION_ID,
        then: schema => schema.required(t('personalInformation.errors.issuedByRequired')),
      }),

    identificationNumber: Yup.string()
      .nullable()
      .when([], {
        is: () =>
          shouldEnforceFieldAsMandatory(
            WorkerProfileField.workerGovtIdNumber,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
          ),
        then: schema =>
          schema
            .required(t('personalInformation.errors.idNumberRequired'))
            .trim()
            .test(minLengthIfFilled(4, t('personalInformation.errors.idNumberMin')))
            .max(20, t('personalInformation.errors.idNumberMax')),
      }),

    governmentDocumentFiles: Yup.array().when([], {
      is: () =>
        shouldEnforceFieldAsMandatory(
          WorkerProfileField.workerGovtIdImage,
          profileFieldConstraintsMap,
          hasReachedConsentForm,
        ),
      then: schema =>
        schema
          .min(1, t('personalInformation.errors.idImageRequired'))
          .required(t('personalInformation.errors.idImageRequired')),
    }),

    pictureUrl: Yup.string()
      .nullable()
      .when([], {
        is: () =>
          shouldEnforceFieldAsMandatory(
            WorkerProfileField.workerProfilePhoto,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
          ),
        then: schema => schema.required(t('personalInformation.errors.profilePictureRequired')),
      }),
  }) as Yup.ObjectSchema<IPersonalInformationSchema>;

export default personalInformationSchema;
